/**
 * Dashboard Utilities
 * Helper functions for data transformation and computation
 */

/**
 * Format ISO date string to relative time
 */
export function formatRelativeTime(isoString: string): string {
  if (!isoString) return "Never";
  const now = new Date();
  const date = new Date(isoString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

/**
 * Strip HTML tags and decode HTML entities from essay content
 */
export function cleanHtmlContent(html: string): string {
  if (!html) return "";
  // Remove HTML tags
  let text = html.replace(/<[^>]*>/g, " ");
  // Decode common HTML entities
  text = text
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  // Collapse multiple spaces into one
  text = text.replace(/\s+/g, " ").trim();
  return text;
}

/**
 * Compute submission status based on deadline and readiness
 */
export function computeSubmissionStatus(
  deadline: string,
  readinessPercent: number,
  expectedPercent: number,
): "on_track" | "behind_schedule" | "past_deadline" {
  const today = new Date();
  const deadlineDate = new Date(deadline);
  const daysUntilDeadline = Math.ceil(
    (deadlineDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (daysUntilDeadline < 0) return "past_deadline";

  // User Simplified Logic:
  // < Expected: "Behind schedule" (Red)
  // >= Expected: "On track" (Green)
  if (readinessPercent < expectedPercent) return "behind_schedule";
  return "on_track";
}

/**
 * Calculate expected progress based on deadline
 */
export function calculateExpectedPercent(deadline: string): number {
  const today = new Date();
  const deadlineDate = new Date(deadline);
  const daysUntilDeadline = Math.ceil(
    (deadlineDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );

  // Assume 60 days is the typical application period
  // Calculate expected progress based on how close to deadline
  if (daysUntilDeadline <= 0) return 100;
  if (daysUntilDeadline >= 60) return 10;

  // Linear progression: 10% at 60 days, 100% at deadline
  return Math.round(10 + (90 * (60 - daysUntilDeadline)) / 60);
}

/**
 * Compute module status based on percentage
 */
export function computeModuleStatus(
  percent: number,
): "on_track" | "behind" | "at_risk" {
  if (percent < 35) return "at_risk";
  if (percent <= 65) return "behind";
  return "on_track";
}

/**
 * Create short label from college name and decision type
 */
export function createShortLabel(
  collegeName: string,
  decisionType: string,
): string {
  // Common abbreviations
  const abbreviations: Record<string, string> = {
    University: "U",
    "Institute of Technology": "Tech",
    "Massachusetts Institute of Technology": "MIT",
    "University of California": "UC",
    "California Institute of Technology": "Caltech",
  };

  let shortName = collegeName;

  // Apply specific abbreviations
  for (const [full, abbr] of Object.entries(abbreviations)) {
    if (collegeName.includes(full)) {
      shortName = collegeName === full ? abbr : collegeName.replace(full, abbr);
      break;
    }
  }

  // If still too long, take first word(s)
  if (shortName.length > 20) {
    const words = shortName.split(" ");
    shortName = words[0];
    if (words[1] && shortName.length + words[1].length < 15) {
      shortName += " " + words[1];
    }
  }

  return `${shortName} ${decisionType}`;
}
